/**
 * Site Analytics - Landing Page / Public Site Tracking
 * 
 * Privacy-friendly, cookie-free analytics tracking.
 * Tracks: pageviews, time on page, scroll depth, exit events.
 * 
 * Usage:
 *   import { initSiteAnalytics } from './analytics';
 *   
 *   // Initialize on landing page
 *   useEffect(() => {
 *     initSiteAnalytics();
 *   }, []);
 */

const ANALYTICS_ENDPOINT = process.env.REACT_APP_BACKEND_URL + '/api/analytics/site/track';

// ==================== SESSION MANAGEMENT ====================

/**
 * Get or create session ID (stored in sessionStorage, cleared when tab closes)
 */
const getSessionId = () => {
  if (typeof window === 'undefined') return null;
  
  let sessionId = sessionStorage.getItem('sr_site_session');
  if (!sessionId) {
    sessionId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('sr_site_session', sessionId);
  }
  return sessionId;
};

// ==================== TRACKING STATE ====================

let maxScrollDepth = 0;
let pageLoadTime = Date.now();
let hasTrackedExit = false;

/**
 * Update max scroll depth as user scrolls
 */
const updateScrollDepth = () => {
  if (typeof window === 'undefined') return;
  
  const scrollTop = window.scrollY || window.pageYOffset;
  const docHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body.scrollHeight
  ) - window.innerHeight;
  
  if (docHeight > 0) {
    const depth = Math.round((scrollTop / docHeight) * 100);
    maxScrollDepth = Math.max(maxScrollDepth, Math.min(depth, 100));
  }
};

// ==================== EVENT SENDING ====================

/**
 * Send analytics event to backend
 */
const sendEvent = async (eventData) => {
  if (typeof window === 'undefined') return;
  
  try {
    const payload = {
      ...eventData,
      session_id: getSessionId(),
      page_url: window.location.href,
      page_path: window.location.pathname,
      referrer: document.referrer || '',
      screen_width: window.screen?.width,
      screen_height: window.screen?.height,
    };
    
    await fetch(ANALYTICS_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true, // Important for beforeunload events
    });
  } catch (e) {
    // Silently fail - don't break the site for analytics
    console.debug('[Analytics] Event send failed:', e);
  }
};

/**
 * Track page exit with engagement metrics
 */
const trackExit = () => {
  if (hasTrackedExit) return;
  hasTrackedExit = true;
  
  const timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000);
  
  sendEvent({
    type: 'exit',
    time_on_page: timeOnPage,
    scroll_depth: maxScrollDepth,
    is_exit: true,
  });
};

// ==================== PUBLIC API ====================

/**
 * Initialize site analytics tracking
 * Call this once on landing page / public pages
 */
export const initSiteAnalytics = () => {
  if (typeof window === 'undefined') return;
  
  // Reset state for new page
  maxScrollDepth = 0;
  pageLoadTime = Date.now();
  hasTrackedExit = false;
  
  // Track pageview immediately
  sendEvent({ type: 'pageview' });
  
  // Setup scroll tracking
  window.addEventListener('scroll', updateScrollDepth, { passive: true });
  
  // Setup exit tracking (multiple triggers for reliability)
  window.addEventListener('beforeunload', trackExit);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      trackExit();
    }
  });
  
  // Cleanup function
  return () => {
    window.removeEventListener('scroll', updateScrollDepth);
    window.removeEventListener('beforeunload', trackExit);
  };
};

/**
 * Track a custom event
 */
export const trackSiteEvent = (eventName, eventData = {}) => {
  sendEvent({
    type: 'event',
    event_name: eventName,
    event_data: eventData,
  });
};

/**
 * Track a button click
 */
export const trackClick = (buttonName, additionalData = {}) => {
  sendEvent({
    type: 'click',
    event_name: `click_${buttonName}`,
    event_data: additionalData,
  });
};

export default {
  initSiteAnalytics,
  trackSiteEvent,
  trackClick,
};
